import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Grid, SearchInput, useDebounce, VirtualTable } from '@common-fe/common-fe';

import { FilterSetters } from '@/utils/hooks/useFilters';

import { ReportTab, ReportTableBaseProps, ReportTypeKeys } from '../../report.types';
import { AddScheduledReportButton } from '../../ScheduledReport';
import AccountTypeDropdown from '../AccountTypeDropdown';
import ClaimStatusSelector from '../ClaimStatusSelector';
import EmployerSelector from '../EmployerSelector';
import ExportButton from '../ExportButton';
import {
  useIsEmployerIsPresent,
  useMultipleEmployerSelectAvailable,
  useShowAppliedSettingsSnackbar,
} from '../hooks';
import MultipleEmployerSelector from '../MultipleEmployerSelector';
import PlaceholderBase from '../PlaceholderBase';
import PlanSelector from '../PlanSelector';
import PlanYearSelector from '../PlanYearSelector';
import ReportLastUpdateDate from '../ReportLastUpdateDate';
import ReportTableWrapper from '../ReportTable/ReportTableWrapper';
import ToggleSSNVisible from '../ToggleSSNVisible';
import TopButtonPortalWrapper from '../TopButtonPortalWrapper';

import {
  DelinquentClaimsReportTableParams,
  DelinquentClaimsReportView,
} from './DelinquentClaimsReport.types';
import DelinquentClaimsViewPicker from './DelinquentClaimsViewPicker';
import { useDelinquentClaimsReportExportCSVQuery, useDelinquentClaimsReportQuery } from './queries';

interface Props extends ReportTableBaseProps {
  filtersValues: DelinquentClaimsReportTableParams;
  onSetFilterValue: FilterSetters<DelinquentClaimsReportTableParams>;
}

const DelinquentClaimsReportTable: React.FC<Props> = ({
  filtersValues,
  onSetFilterValue,
  isPreviewMode,
  defaultEmployerId,
}) => {
  const isEmployerIsPresent = useIsEmployerIsPresent(filtersValues, {
    isMultipleEmployerMode: true,
  });
  const isMultipleEmployerAvailable = useMultipleEmployerSelectAvailable();
  const [isAutoFocus, setAutoFocus] = useState(false);
  const debouncedSearch = useDebounce(filtersValues.searchString);

  const {
    isHasNextPage,
    total,
    isLoading,
    headers,
    nodes,
    lastTimeUpdateDate,
    columnWidths,
    isExportDisabled,
    planOptions,
    isEmptyPlanOptions,
    handleLoadMore,
    onChangeHeaderSize,
  } = useDelinquentClaimsReportQuery({
    ...filtersValues,
    searchString: debouncedSearch,
    isPreviewMode,
    isMultipleEmployer: isMultipleEmployerAvailable,
  });

  const isPlanFilterDisabled =
    !isEmployerIsPresent || (!!isMultipleEmployerAvailable && filtersValues.employerIds?.length !== 1);

  const isByClaimType = useMemo(
    () => filtersValues.reportView === DelinquentClaimsReportView.ByClaim,
    [filtersValues.reportView]
  );

  // Clear the Account Type filter when it is hidden so a stale value is not applied to the query.
  useEffect(() => {
    if (isByClaimType && filtersValues.accountTypes.length) {
      onSetFilterValue.accountTypes([]);
      onSetFilterValue.planIds([]);
    } else {
      onSetFilterValue.claimStatuses([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isByClaimType]);

  const childrenNode = useMemo(() => {
    if (!isEmployerIsPresent) {
      return (
        <PlaceholderBase>
          <PlaceholderBase.Link onClick={() => setAutoFocus(true)} />
        </PlaceholderBase>
      );
    }

    if (!isLoading && !total) {
      if (filtersValues.searchString.trim()) {
        return (
          <PlaceholderBase isLarge>
            <PlaceholderBase.Search />
          </PlaceholderBase>
        );
      }
      return (
        <PlaceholderBase isLarge>
          <PlaceholderBase.Default />
        </PlaceholderBase>
      );
    }
    return (
      <VirtualTable
        testId="delinquent-claims-report-table"
        height={isPreviewMode ? 'calc(100vh - 545px)' : 'calc(80vh - 160px)'}
        isLoading={isLoading}
        onResizeHeaders={onChangeHeaderSize}
        onLoadMore={handleLoadMore}
        isHasMore={isHasNextPage}
        columnSizes={columnWidths}
        isIndexMode={true}
        headers={headers}
        items={nodes}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPreviewMode,
    columnWidths,
    filtersValues.employerIds,
    filtersValues.employerId,
    filtersValues.searchString,
    handleLoadMore,
    headers,
    isHasNextPage,
    isLoading,
    nodes,
    onChangeHeaderSize,
    total,
  ]);

  const { handleExport: handleExportCSV, isLoading: isExportLoading } =
    useDelinquentClaimsReportExportCSVQuery({
      ...filtersValues,
      isMultipleEmployer: isMultipleEmployerAvailable,
    });

  const handleShowAppliedFilterSnackbar = useShowAppliedSettingsSnackbar();
  const handleToggleSSN = useCallback(
    (value: boolean) => {
      onSetFilterValue.isSSNShowed(value);
      handleShowAppliedFilterSnackbar();
    },
    [handleShowAppliedFilterSnackbar, onSetFilterValue]
  );

  return (
    <Box direction="column">
      <TopButtonPortalWrapper
        isPreviewMode={isPreviewMode}
        infoNode={
          <ReportLastUpdateDate
            isWideMode
            isLoading={isLoading}
            key={`${filtersValues.employerId}`}
            value={lastTimeUpdateDate}
          >
            <Box margin={{ left: 'spacing16' }}>
              <DelinquentClaimsViewPicker
                disabled={!isEmployerIsPresent}
                value={filtersValues.reportView}
                onChangeValue={onSetFilterValue.reportView}
              />
            </Box>
          </ReportLastUpdateDate>
        }
        buttonNode={
          <Box direction="row">
            <AddScheduledReportButton
              employerId={filtersValues.employerId}
              employerIds={filtersValues.employerIds}
              isMultipleEmployer={isMultipleEmployerAvailable}
              type={ReportTypeKeys.DelinquentClaims}
              tab={ReportTab.Report}
            />
            <Box margin={{ left: 'spacing12' }}>
              <ExportButton
                isLoading={isExportLoading}
                onClick={handleExportCSV}
                disabled={!isEmployerIsPresent || isExportDisabled}
                text="Download Report (CSV)"
              />
            </Box>
          </Box>
        }
      >
        <ReportTableWrapper
          total={total}
          isLoading={isLoading}
          isPreviewMode={isPreviewMode}
          previewHeaderChildren={
            <DelinquentClaimsViewPicker
              disabled={!isEmployerIsPresent}
              value={filtersValues.reportView}
              onChangeValue={onSetFilterValue.reportView}
            />
          }
          headerChildren={
            <ToggleSSNVisible
              disabled={!isEmployerIsPresent}
              value={filtersValues.isSSNShowed}
              onChangeValue={handleToggleSSN}
            />
          }
          filters={
            <Box direction="row" fill>
              <Grid
                fill
                columns={{
                  count: (isPreviewMode ? 4 : 6) - (isByClaimType ? 1 : 0),
                  size: 'auto',
                }}
                gap="spacing12"
              >
                {!isPreviewMode && (
                  <SearchInput
                    testId="delinquent-claims-report"
                    placeholder="Search"
                    value={filtersValues.searchString}
                    disabled={!isEmployerIsPresent}
                    onChange={onSetFilterValue.searchString}
                  />
                )}
                {isMultipleEmployerAvailable ? (
                  <MultipleEmployerSelector
                    disabled={isPreviewMode && !!defaultEmployerId}
                    onSetAutoFocus={setAutoFocus}
                    isAutoFocus={isAutoFocus}
                    values={filtersValues.employerIds}
                    onChangeValues={onSetFilterValue.employerIds}
                  />
                ) : (
                  <EmployerSelector
                    disabled={isPreviewMode && !!defaultEmployerId}
                    onSetAutoFocus={setAutoFocus}
                    isAutoFocus={isAutoFocus}
                    value={filtersValues.employerId}
                    onChangeValue={onSetFilterValue.employerId}
                  />
                )}
                <PlanYearSelector
                  disabled={isPlanFilterDisabled}
                  value={filtersValues.planYearPeriod}
                  onChangeValue={onSetFilterValue.planYearPeriod}
                />
                {!!isByClaimType && (
                  <ClaimStatusSelector
                    disabled={!isEmployerIsPresent}
                    value={filtersValues.claimStatuses}
                    onChangeValue={onSetFilterValue.claimStatuses}
                  />
                )}
                {!isByClaimType && (
                  <Box key={`plan-selector ${isLoading}`}>
                    <PlanSelector
                      disabled={isPlanFilterDisabled || isEmptyPlanOptions}
                      options={planOptions}
                      values={filtersValues.planIds}
                      onChangeValues={onSetFilterValue.planIds}
                    />
                  </Box>
                )}

                {!isByClaimType && (
                  <AccountTypeDropdown
                    disabled={!isEmployerIsPresent}
                    values={filtersValues.accountTypes}
                    onChangeValues={onSetFilterValue.accountTypes}
                  />
                )}
              </Grid>
            </Box>
          }
        >
          {childrenNode}
        </ReportTableWrapper>
      </TopButtonPortalWrapper>
    </Box>
  );
};

export default DelinquentClaimsReportTable;
